import React from 'react';
import CalculationEditor from './CalculationEditor';
import ConditionEditor, { ConditionBadge } from './ConditionEditor';
import { FIELD_TYPES, fieldTypeLabel } from './fieldTypes';
import { conditionOf, newCondition } from './formConditions';
import { useIndexDragReorder } from './useDragReorder';
import { ACTION_BTN, DANGER_ICON_BTN, GRIP_ICON_BTN, ICON_BTN } from './ui';

const TrashIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const PlusIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
  </svg>
);

// Six-dot grip: the standard "pick me up and move me" affordance.
const GripIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
    <circle cx="7" cy="5" r="1.5" />
    <circle cx="13" cy="5" r="1.5" />
    <circle cx="7" cy="10" r="1.5" />
    <circle cx="13" cy="10" r="1.5" />
    <circle cx="7" cy="15" r="1.5" />
    <circle cx="13" cy="15" r="1.5" />
  </svg>
);

// Stand-in for the hidden rail grip: a faint vertical three-dot mark that says
// "there is something here" before the pointer arrives.
const RailHintIcon = () => (
  <svg className="w-3 h-3" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true">
    <circle cx="6" cy="2" r="1.1" />
    <circle cx="6" cy="6" r="1.1" />
    <circle cx="6" cy="10" r="1.1" />
  </svg>
);

const ChevronIcon = ({ expanded }) => (
  <svg
    className={`w-4 h-4 shrink-0 text-purple-300 transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

// Editor card for a single field in the Form Builder. Collapsed it shows just
// the question and its type; expanded it holds the type selector,
// question/name inputs and the type-specific editors (text presets, dropdown
// options and saved presets, calculation builder). Reordering lives on a rail
// that only appears when the pointer is over the left edge of the card; adding
// a question is done from the gaps between cards (InsertRow).
//
// A Dropdown's options get a rail of their own, one level in: the same
// three-dot hint beside the list, and a grip on every option.
//
// Ticking Conditional opens the condition editor under the type row; collapsed,
// a conditional question says what it waits for in a badge beside its type.
// `formEntries` is the whole form as a tree — the condition can wait on any
// question in it — and `conditionCheck` is this question's result from
// formConditions.checkConditions.
//
// `alert`/`confirm` are the in-app dialogs from useDialog(), passed down rather
// than pulled from context here so the card stays presentational.
const FieldEditorCard = ({
  field,
  index,
  fields,
  formEntries,
  conditionCheck,
  setCondition,
  expanded,
  onToggleExpanded,
  isDragging,
  itemProps,
  handleProps,
  presets,
  updateField,
  removeField,
  addSelectOption,
  updateSelectOption,
  removeSelectOption,
  moveSelectOptionTo,
  onApplyPreset,
  onSavePreset,
  onDeletePreset,
  addPresetOption,
  updatePresetOption,
  removePresetOption,
  alert,
}) => {
  const options = Array.isArray(field.options) ? field.options : [];
  const summary = field.label?.trim() || field.name?.trim() || 'Untitled question';
  const conditional = !!conditionOf(field);

  const {
    draggingIndex: draggingOption,
    rowProps: optionRowProps,
    handleProps: optionHandleProps,
  } = useIndexDragReorder({
    count: options.length,
    onReorder: (from, to) => moveSelectOptionTo(field.id, from, to),
  });
  // While an option is in flight every grip stays out, so the pointer can leave
  // the rail without the row it is carrying losing its handle.
  const optionDragging = draggingOption !== null;

  return (
    <div
      {...itemProps}
      id={`entry-${field.id}`}
      className={`relative bg-gray-700/30 border rounded-lg pl-11 pr-4 py-4 transition-colors ${
        isDragging ? 'border-purple-400 ring-2 ring-purple-500/50 opacity-70' : 'border-gray-600'
      }`}
    >
      {/* Reorder rail: hidden until the pointer reaches the left edge of the
          card, with a faint three-dot mark standing in its place so the grip
          is discoverable. Keyboard focus opens it too, but only :focus-visible
          focus — a mouse click leaves focus on the grip it hit, which would
          otherwise pin the rail open for good. The grip sits level with the
          title row, open or closed. */}
      <div className="group/rail absolute left-0 inset-y-0 w-11 flex justify-center pt-5">
        <div className="relative self-start">
          <span
            aria-hidden="true"
            className={`pointer-events-none absolute inset-0 flex items-center justify-center text-gray-500 transition-opacity duration-150 group-hover/rail:opacity-0 group-[:has(:focus-visible)]/rail:opacity-0 ${
              isDragging ? 'opacity-0' : 'opacity-70'
            }`}
          >
            <RailHintIcon />
          </span>
          <div
            className={`relative flex transition-opacity duration-150 group-hover/rail:opacity-100 group-[:has(:focus-visible)]/rail:opacity-100 ${
              isDragging ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <span
              {...handleProps}
              role="button"
              tabIndex={0}
              aria-label={`Reorder question ${index + 1}. Press the up or down arrow keys to move it.`}
              title="Drag to move this question"
              className={`${ICON_BTN} cursor-grab active:cursor-grabbing select-none`}
            >
              <GripIcon />
            </span>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onToggleExpanded}
          aria-expanded={expanded}
          className="flex-1 min-w-0 flex items-center gap-3 bg-transparent hover:bg-transparent text-left px-0 py-1 rounded"
        >
          <ChevronIcon expanded={expanded} />
          <span className="text-xs text-gray-500 tabular-nums shrink-0">{index + 1}</span>
          <span className={`truncate ${field.label?.trim() ? 'text-white' : 'text-gray-400 italic'}`}>
            {summary}
          </span>
          <span className="shrink-0 text-xs px-2 py-0.5 rounded-full bg-purple-500/15 text-purple-200 border border-purple-500/30">
            {fieldTypeLabel(field.type)}
            {field.type === 'select' && field.multiple ? ' · multi' : ''}
          </span>
          {conditional && <ConditionBadge check={conditionCheck} editing={expanded} />}
          {field.required && <span className="shrink-0 text-red-400 text-sm">*</span>}
        </button>
        <button
          type="button"
          onClick={() => removeField(field.id)}
          className={DANGER_ICON_BTN}
          title="Delete this question"
          aria-label="Delete this question"
        >
          <TrashIcon />
        </button>
      </div>

      {expanded && (
        <div className="mt-4">
          <div className="flex flex-wrap items-center gap-4 mb-3">
            <select
              value={field.type}
              onChange={(e) => updateField(field.id, { type: e.target.value })}
              aria-label="Question type"
              className="bg-gray-600 border border-gray-500 rounded px-3 py-1 text-white text-sm"
            >
              {FIELD_TYPES.map((type) => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
            {/* Contact fields always require a name, so the toggle would be a no-op. */}
            {field.type !== 'contact' && (
              <label className="flex items-center text-sm text-gray-400">
                <input
                  type="checkbox"
                  checked={field.required || false}
                  onChange={(e) => updateField(field.id, { required: e.target.checked })}
                  className="mr-2"
                />
                Required
              </label>
            )}
            <label className="flex items-center text-sm text-gray-400">
              <input
                type="checkbox"
                checked={conditional}
                onChange={(e) => setCondition(field.id, e.target.checked ? newCondition(null) : null)}
                className="mr-2"
              />
              Conditional
            </label>
          </div>

          {conditional && (
            <div className="mb-3">
              <ConditionEditor
                entry={field}
                entries={formEntries}
                subject="question"
                problem={conditionCheck?.problem}
                onChange={(condition) => setCondition(field.id, condition)}
              />
            </div>
          )}

          {/* The question is what the inspector reads, so it is the required one.
              The field name is an optional short alias for report and Excel
              headings; reports fall back to the question text without it. */}
          <input
            type="text"
            value={field.label || ''}
            onChange={(e) => updateField(field.id, { label: e.target.value })}
            placeholder="Question text (required)"
            aria-label="Question text"
            required
            className="w-full bg-gray-600 border border-gray-500 rounded px-3 py-2 text-white placeholder-gray-400"
          />
          <input
            type="text"
            value={field.name || ''}
            onChange={(e) => updateField(field.id, { name: e.target.value })}
            placeholder="Field name (optional) — short heading for reports and Excel"
            aria-label="Field name"
            className="w-full mt-2 bg-gray-600 border border-gray-500 rounded px-3 py-2 text-white placeholder-gray-400"
          />

          {field.type === 'text' && (
            <div className="mt-3 space-y-2">
              <div className="text-sm font-medium text-gray-200">Text presets</div>
              {(field.presets || []).map((preset, presetIndex) => (
                <div key={`preset-${presetIndex}`} className="flex flex-wrap items-center gap-2">
                  <input
                    type="text"
                    value={preset}
                    onChange={(e) => updatePresetOption(field.id, presetIndex, e.target.value)}
                    placeholder={`Preset ${presetIndex + 1}`}
                    className="flex-1 min-w-[180px] bg-gray-600 border border-gray-500 rounded px-3 py-2 text-white placeholder-gray-400"
                  />
                  <button
                    type="button"
                    onClick={() => removePresetOption(field.id, presetIndex)}
                    className={DANGER_ICON_BTN}
                    title="Remove this preset"
                    aria-label={`Remove text preset ${presetIndex + 1}`}
                  >
                    <TrashIcon />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => addPresetOption(field.id)}
                className={`${ACTION_BTN} mt-2`}
              >
                <PlusIcon />
                Add preset
              </button>
            </div>
          )}

          {field.type === 'contact' && (
            <div className="mt-3 rounded border border-gray-600 bg-gray-700/40 px-3 py-2 text-sm text-gray-300">
              Collects name, phone, email and address. Name is always required; the rest are optional.
            </div>
          )}

          {field.type === 'select' && (
            <div className="mt-3 grid gap-4 md:grid-cols-[minmax(0,1fr)_15rem]">
              <div className="pl-9 space-y-2">
                {/* Option rail: the question rail's trick one level in. A single
                    three-dot mark sits beside the whole list — the side of the
                    open question saying the controls are here — and stands down
                    once the pointer arrives and every option shows its grip. As
                    on the card, only :focus-visible focus opens it, so a mouse
                    click can't pin it open. */}
                <div className="group/optrail relative space-y-2">
                  <span
                    aria-hidden="true"
                    className={`pointer-events-none absolute -left-9 inset-y-0 w-9 flex items-center justify-center text-gray-500 transition-opacity duration-150 group-hover/optrail:opacity-0 group-[:has(:focus-visible)]/optrail:opacity-0 ${
                      optionDragging ? 'opacity-0' : 'opacity-70'
                    }`}
                  >
                    <RailHintIcon />
                  </span>
                  {options.map((option, optionIndex) => (
                    <div
                      key={`option-${optionIndex}`}
                      {...optionRowProps(optionIndex)}
                      className={`relative flex flex-wrap items-center gap-2 rounded-lg ${
                        draggingOption === optionIndex ? 'ring-2 ring-purple-500/50' : ''
                      }`}
                    >
                      <span
                        {...optionHandleProps(optionIndex)}
                        role="button"
                        tabIndex={0}
                        aria-label={`Reorder option ${optionIndex + 1}. Press the up or down arrow keys to move it.`}
                        title="Drag to move this option"
                        className={`${GRIP_ICON_BTN} absolute -left-9 top-1/2 -translate-y-1/2 cursor-grab active:cursor-grabbing select-none transition-opacity duration-150 group-hover/optrail:opacity-100 group-[:has(:focus-visible)]/optrail:opacity-100 ${
                          optionDragging ? 'opacity-100' : 'opacity-0'
                        }`}
                      >
                        <GripIcon />
                      </span>
                      <input
                        type="text"
                        value={option}
                        onChange={(e) => updateSelectOption(field.id, optionIndex, e.target.value)}
                        placeholder={`Option ${optionIndex + 1}`}
                        className="flex-1 min-w-[140px] bg-gray-600 border border-gray-500 rounded px-3 py-2 text-white placeholder-gray-400"
                      />
                      <button
                        type="button"
                        onClick={() => removeSelectOption(field.id, optionIndex)}
                        className={DANGER_ICON_BTN}
                        title="Remove this option"
                        aria-label={`Remove option ${optionIndex + 1}`}
                      >
                        <TrashIcon />
                      </button>
                    </div>
                  ))}
                </div>

                <div className="flex flex-wrap gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => addSelectOption(field.id)}
                    className={ACTION_BTN}
                  >
                    <PlusIcon />
                    Add option
                  </button>
                  <button
                    type="button"
                    onClick={() => onSavePreset(field)}
                    className={ACTION_BTN}
                    title="Save these options as a reusable set"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 4h11l3 3v13a1 1 0 01-1 1H5a1 1 0 01-1-1V5a1 1 0 011-1z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 4v5h7M8 15h8" />
                    </svg>
                    Save as preset
                  </button>
                </div>

                <label className="flex items-center text-sm text-gray-300 pt-1">
                  <input
                    type="checkbox"
                    checked={!!field.multiple}
                    onChange={(e) => updateField(field.id, { multiple: e.target.checked })}
                    className="mr-2"
                  />
                  Allow multiple answers
                </label>
              </div>

              {/* Saved option sets for this form. Selecting one fills the
                  options above with its entries, in the saved order. */}
              <div className="rounded-lg border border-gray-600 bg-gray-800/40 p-3">
                <div className="text-sm font-medium text-gray-200 mb-2">Presets</div>
                {presets.length === 0 ? (
                  <p className="text-xs text-gray-500">
                    No presets yet. Enter some options and choose “Save as preset”.
                  </p>
                ) : (
                  <div className="space-y-1">
                    {presets.map((preset) => (
                      <div key={preset.id} className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => onApplyPreset(field, preset)}
                          title={preset.options.join(', ')}
                          className="flex-1 min-w-0 flex items-center justify-between gap-2 bg-purple-500/10 hover:bg-purple-500/25 border border-purple-500/30 text-left text-sm text-purple-100 px-3 py-2 rounded transition-colors"
                        >
                          <span className="truncate">{preset.name}</span>
                          <span className="shrink-0 text-xs text-purple-300/80">
                            {preset.options.length}
                          </span>
                        </button>
                        <button
                          type="button"
                          onClick={() => onDeletePreset(preset)}
                          className={DANGER_ICON_BTN}
                          title={`Delete the ${preset.name} preset`}
                          aria-label={`Delete the ${preset.name} preset`}
                        >
                          <TrashIcon />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {field.type === 'calculation' && (
            <CalculationEditor
              field={field}
              fields={fields}
              updateField={updateField}
              alert={alert}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default FieldEditorCard;
